import {
  Box,
  Typography,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material'
import {
  ChevronLeft,
  ChevronRight,
  FirstPage,
  LastPage,
} from '@mui/icons-material'

type TableHeaderProps = {
  page: number
  pageSize: number
  rowCount: number
  onPageChange: (newPage: number) => void
  setPageSize?: (newPageSize: number) => void
  isDarkMode?: boolean
  position?: 'top' | 'bottom'
}

export default function TableHeader({
  page,
  pageSize,
  rowCount,
  onPageChange,
  setPageSize,
  isDarkMode = false,
  position = 'top',
}: TableHeaderProps) {
  const totalPages = Math.ceil(rowCount / pageSize)
  const from = rowCount === 0 ? 0 : page * pageSize + 1
  const to = Math.min(rowCount, (page + 1) * pageSize)

  const colors = {
    background: isDarkMode ? '#1D325A' : '#EDF3F9',
    border: isDarkMode ? '#374151' : '#DDE6EF',
    text: isDarkMode ? '#FFFFFF' : '#003F61',
    iconColor: isDarkMode ? '#FFFFFF' : '#003F61',
    selectBg: isDarkMode ? '#202B40' : '#FFFFFF',
    selectBorder: isDarkMode ? '#374151' : '#C8D5E1',
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        px: 1,
        py: 0.5,
        borderBottom: position === 'top' ? `1px solid ${colors.border}` : 'none',
        borderTop: position === 'bottom' ? `1px solid ${colors.border}` : 'none',
        backgroundColor: colors.background,
        width: '100%',
        borderBottomLeftRadius: '0px',
        borderBottomRightRadius: '0px',
        borderTopLeftRadius: '0px',
        borderTopRightRadius: '0px',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{ width: '60%', justifyContent: 'end' }}
      >
        <Box display="flex" alignItems="center" sx={{}}>
          <IconButton disabled={page === 0} onClick={() => onPageChange(0)}>
            <LastPage sx={{ color: colors.iconColor, opacity: page === 0 ? 0.3 : 1, fontSize: 18 }} />
          </IconButton>
          <IconButton
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronRight sx={{ color: colors.iconColor, opacity: page === 0 ? 0.3 : 1, fontSize: 18 }} />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              direction: 'rtl',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontWeight: 300,
                fontSize: '14px',
                lineHeight: '24px',
                color: colors.text,
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              עמוד
            </Typography>

            <Typography
              sx={{
                fontWeight: 900,
                fontSize: '16px',
                lineHeight: '24px',
                color: colors.text,
                mx: 0.5,
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              {page + 1}
            </Typography>

            <Typography
              sx={{
                fontWeight: 300,
                fontSize: '14px',
                lineHeight: '24px',
                color: colors.text,
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              מתוך
            </Typography>

            <Typography
              sx={{
                fontWeight: 900,
                fontSize: '16px',
                lineHeight: '24px',
                color: colors.text,
                mx: 0.5,
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              {totalPages}
            </Typography>
          </Box>
          <IconButton
            disabled={page >= totalPages - 1}
            onClick={() => {
              onPageChange(page + 1)
            }}
          >
            <ChevronLeft sx={{ color: colors.iconColor, opacity: page >= totalPages - 1 ? 0.3 : 1, fontSize: 18 }} />
          </IconButton>
          <IconButton
            disabled={page >= totalPages - 1}
            onClick={() => onPageChange(totalPages - 1)}
          >
            <FirstPage sx={{ color: colors.iconColor, opacity: page >= totalPages - 1 ? 0.3 : 1, fontSize: 18 }} />
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" gap={1} sx={{}}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '24px',
              color: colors.text,
              fontFamily: 'Rubik, sans-serif',
            }}
          >
            {from}
          </Typography>
          <Typography
            sx={{
              fontWeight: 300,
              fontSize: 'clamp(12px, 0.85vw, 16px)',
              lineHeight: '24px',
              color: colors.text,
              fontFamily: 'Rubik, sans-serif',
            }}
          >
            עד
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '24px',
              color: colors.text,
              fontFamily: 'Rubik, sans-serif',
            }}
          >
            {to}
          </Typography>
          <Typography
            sx={{
              fontWeight: 300,
              fontSize: '14px',
              lineHeight: '24px',
              color: colors.text,
              fontFamily: 'Rubik, sans-serif',
            }}
          >
            מתוך
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '24px',
              color: colors.text,
              fontFamily: 'Rubik, sans-serif',
            }}
          >
            {rowCount}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flex: 1,
          justifyContent: 'end',
          color: colors.text,
        }}
      >
        הצג
        <FormControl size="small" sx={{ minWidth: '76px' }}>
          <Select
            id="demo-simple-select"
            sx={{
              backgroundColor: colors.selectBg,
              color: colors.text,
              fontSize: '14px',
              height: 'clamp(28px, 2.4vw, 38px)',
              borderRadius: '8px',
              border: `0.1px solid ${colors.selectBorder}`,
              '& .MuiSelect-select': {
                paddingRight: '12px !important',
                paddingLeft: '28px !important',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
              '& .MuiSelect-icon': {
                right: 'auto',
                left: '4px',
                color: colors.iconColor,
              },
            }}
            value={pageSize}
            onChange={e => {
              setPageSize?.(Number(e.target.value))
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: colors.selectBg,
                  color: colors.text,
                }
              }
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  )
}
